import React,
{
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  loginRequest,
  registerRequest,
  meRequest,
  setAuthToken,
  type SafeUser,
} from "../lib/api";

export type AuthCtx = {
  user: SafeUser | null;
  token: string | null;
  loading: boolean;

  setAuth: (token: string | null, user: SafeUser | null) => void;
  clearAuth: () => Promise<void>;

  // Primary actions
  signIn: (email: string, password: string) => Promise<{ error?: string }>;
  signUp: (
    name: string,
    email: string,
    password: string
  ) => Promise<{ error?: string; needsConfirm?: boolean }>;
  signOut: () => Promise<void>;
};

const Ctx = createContext<AuthCtx | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [token, setTokenState] = useState<string | null>(null);
  const [user, setUserState] = useState<SafeUser | null>(null);
  const [loading, setLoading] = useState(true);

  /* =========================
     Load saved token on mount
     ========================= */
  useEffect(() => {
    const storedToken = localStorage.getItem("authToken");
    const storedUser = localStorage.getItem("authUser");

    if (!storedToken) {
      setLoading(false);
      return;
    }

    setTokenState(storedToken);
    setAuthToken(storedToken);

    // Optimistically restore the cached user for instant paint…
    if (storedUser) {
      try {
        setUserState(JSON.parse(storedUser));
      } catch {
        localStorage.removeItem("authUser");
      }
    }

    // …then validate the token against the server. An expired/invalid
    // token 401s, which clears the session and redirects to /login
    // (handled centrally in lib/api.ts).
    meRequest()
      .then(({ user }) => {
        setUserState(user);
        localStorage.setItem("authUser", JSON.stringify(user));
      })
      .catch(() => {
        // 401 already handled by api.ts; ignore transient network errors
      })
      .finally(() => setLoading(false));
  }, []);

  /* =========================
     Helpers
     ========================= */
  function setAuth(nextToken: string | null, nextUser: SafeUser | null) {
    setTokenState(nextToken);
    setUserState(nextUser);

    setAuthToken(nextToken);

    if (nextToken) {
      localStorage.setItem("authToken", nextToken);
      localStorage.setItem("authUser", JSON.stringify(nextUser));
    } else {
      localStorage.removeItem("authToken");
      localStorage.removeItem("authUser");
    }
  }

  async function clearAuth() {
    try { localStorage.removeItem("demoSession"); } catch { /* private mode */ }
    setAuth(null, null);
  }

  /* =========================
     Main login
     ========================= */
  async function signIn(email: string, password: string) {
    try {
      const { token, user } = await loginRequest(email, password);

      setAuth(token, user);
      return {};
    } catch (err: unknown) {
      return { error: err instanceof Error ? err.message : "Login failed" };
    }
  }

  /* =========================
     Sign up
     ========================= */
  async function signUp(name: string, email: string, password: string) {
    try {
      // The name typed on the sign-up form. It greets the user and is the
      // fallback company name on proposals, so an email prefix won't do.
      const displayName = name.trim() || email.split("@")[0] || email;
      const { token, user } = await registerRequest(
        displayName,
        email,
        password
      );

      if (token) setAuth(token, user);

      return { needsConfirm: false };
    } catch (err: unknown) {
      return { error: err instanceof Error ? err.message : "Signup failed" };
    }
  }

  /* =========================
     Logout
     ========================= */
  async function signOut() {
    clearAuth();
  }

  /* =========================
     Context value
     ========================= */
  const value = useMemo<AuthCtx>(
    () => ({
      user,
      token,
      loading,
      setAuth,
      clearAuth,
      signIn,
      signUp,
      signOut,
    }),
    [user, token, loading]
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

// The provider and its hook belong together; splitting them across files to
// satisfy Fast Refresh would ripple through every importer for no runtime gain.
// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
}
